function getOrdinalSuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

const DAY_OF_WEEK_MAP = {
  'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
  'Thursday': 4, 'Friday': 5, 'Saturday': 6
};

/**
 * Generate venue dates for a given date range, respecting day-of-week
 * transitions, blackout dates, and existing events.
 *
 * @param {Object} options
 * @param {Object} options.venue - Must have id, day_of_week. May have new_day_of_week, day_change_date.
 * @param {Date}   options.startDate - First date to consider (inclusive)
 * @param {Date}   options.endDate - Last date to consider (inclusive)
 * @param {Set}    options.blackoutDates - Set of 'YYYY-MM-DD' strings to skip
 * @param {Set}    options.existingEventNames - Set of formatted names to skip (dedup)
 */
function generateVenueDates({ venue, startDate, endDate, blackoutDates, existingEventNames }) {
  const targetDay = DAY_OF_WEEK_MAP[venue.day_of_week];

  const changeDateObj = venue.day_change_date
    ? new Date(venue.day_change_date + 'T00:00:00')
    : null;
  const newTargetDay = changeDateObj != null
    ? DAY_OF_WEEK_MAP[venue.new_day_of_week]
    : null;

  const generated = [];
  const currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0);

  while (currentDate <= endDate) {
    const effectiveTarget = (changeDateObj && currentDate >= changeDateObj)
      ? newTargetDay
      : targetDay;

    if (currentDate.getDay() === effectiveTarget) {
      const year = currentDate.getFullYear();
      const month = currentDate.toLocaleString('en-US', { month: 'short' });
      const day = currentDate.getDate();
      const dateStr = `${year}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const suffix = getOrdinalSuffix(day);
      const formattedDate = `${month} ${day}${suffix} ${year}`;

      if (!blackoutDates.has(dateStr) && !existingEventNames.has(formattedDate)) {
        generated.push({
          id: `generated_${dateStr}`,
          event_date: dateStr,
          event_name: formattedDate,
          venue_id: venue.id,
          is_generated: true,
        });
      }
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return generated;
}

/**
 * Filter out existing DB events that fall on the OLD day-of-week on or after
 * the venue's day_change_date. These are stale events for shows that won't
 * happen, so comedians shouldn't be able to apply for them.
 *
 * Returns the events array unchanged if no transition is configured.
 */
function filterStaleEvents(existingEvents, venue) {
  if (!venue.day_change_date || !venue.new_day_of_week) {
    return existingEvents;
  }

  const oldDay = DAY_OF_WEEK_MAP[venue.day_of_week];
  const changeDate = venue.day_change_date; // 'YYYY-MM-DD' string, comparable

  return existingEvents.filter(event => {
    if (event.venue_id !== venue.id) return true;
    if (event.event_date < changeDate) return true;
    const eventDayOfWeek = new Date(event.event_date + 'T00:00:00').getDay();
    return eventDayOfWeek !== oldDay;
  });
}

module.exports = { generateVenueDates, filterStaleEvents, getOrdinalSuffix, DAY_OF_WEEK_MAP };
